import React, { PureComponent } from 'react';
import { withTranslation, TFunction } from 'react-i18next';
import anime from 'animejs';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import Location from '@solvhealth/types/interfaces/Location';
import Booking from '@solvhealth/types/interfaces/Booking';
import { Stack } from '@solvhealth/jigsaw-v1';
import InsuranceCard from './InsuranceCard';
import { ENTER_MANUALLY_FROM_TIMEOUT, SHOW_TAKING_TOO_LONG } from '../../constants/index';
import { analyticsTrackEvent } from '../../core/analytics';
import MainCtaButton from './components/MainCtaButton';
import { OriginType } from '../../core/util/bookingFlowRoutingTypes';
import { ImageType } from '../../sagas/images';
import { isPhotoOnlyInsuranceFlow } from '../../core/util/location';
import { getAddInsuranceActions, AddInsuranceActions } from '~/routes/addInsurance';
import {
  setMetaPaperworkInsuranceIdBackId,
  setMetaPaperworkInsuranceIdBackImgUrl,
  setMetaPaperworkInsuranceIdFrontId,
  setMetaPaperworkInsuranceIdFrontImgUrl,
} from '~/ducks/paperworkMeta';
import { validateFamilyBooking } from '~/util/validateFamilyBooking';

import { CARD_WIDTH as h22022CardWidth } from './util/H22022CardComponents';
import { CARD_WIDTH as defaultCardWidth } from './util/CardComponents';
import { getBooleanFlag } from '~/core/launch-darkly/flags';
import Button, { ButtonVariations } from '../SolvPatternLibrary/Button';
import { WhiteLabelPaletteName } from '~/hooks/useWhiteLabelProperties';
import { WhiteLabelWrapperWithTheme } from '~/components/util/WhiteLabelWrapperWithTheme';

const TAKE_TOO_LONG_TIMEOUT = 30000; // 30 seconds in milliseconds

const StyledSkipInsuranceButton = styled(Button).withConfig({ componentId: 'sc-19wfrlr-0' })`
  margin-top: 5px;
  margin-left: auto;
  margin-right: auto;
  cursor: pointer;
  -webkit-font-smoothing: antialiased;
`;

const CtaContainerGrid = styled(Grid).withConfig({ componentId: 'sc-19wfrlr-1' })`
  margin-top: 15px;
`;

const ANIMATION_CLASS_NAME = 'anime-target';

type OwnProps = {
  // @ts-expect-error ts-migrate(2749) FIXME: 'InsuranceCardShape' refers to a value, but is bei... Remove this comment to see the full error message
  insuranceCard?: InsuranceCardShape;
  onClickFront?: (...args: any[]) => any;
  onClickBack?: (...args: any[]) => any;
  handleFileChange: (
    imageType: ImageType,
    location: Location,
    afterChangeActions: AddInsuranceActions
  ) => any;
  handleEnterManually: (...args: any[]) => any;
  handleSkipBack?: (...args: any[]) => any;
  skipBack?: boolean;
  handleSkipInsurance?: () => void;
  location: Location;
  origin: OriginType;
  t: TFunction<['addInsurance', 'common']>;
  booking?: Booking;
  hideCta?: boolean;
  theme?: WhiteLabelPaletteName;
  isSRPorCDP?: boolean;
};

type State = any;

class MobileAddInsurance extends PureComponent<OwnProps, State> {
  static defaultProps: Partial<OwnProps> = {
    insuranceCard: {
      front: {},
      back: {},
    },
  };

  timeouts: number[] = [];

  state = {
    showTakeTooLong: false,
  };

  componentDidUpdate = (prevProps: any) => {
    const { insuranceCard, skipBack } = this.props;
    const prevStarted = this.isScanStarted(prevProps);
    const nowStarted = this.isScanStarted(this.props);

    if (!isPhotoOnlyInsuranceFlow(this.props.location) && !prevStarted && nowStarted) {
      this.timeouts.push(
        setTimeout(() => {
          this.setState({ showTakeTooLong: true });

          analyticsTrackEvent(SHOW_TAKING_TOO_LONG);
        }, TAKE_TOO_LONG_TIMEOUT)
      );
    }

    if (skipBack) return;
    if (!prevProps.insuranceCard.back.scanStarted && insuranceCard.back.scanStarted) {
      const kis511H22022BookingFlow = getBooleanFlag('kis511H22022BookingFlow');
      const cardWidth =
        kis511H22022BookingFlow && this.props.isSRPorCDP ? h22022CardWidth : defaultCardWidth;

      this.timeouts.push(
        setTimeout(
          () =>
            anime({
              targets: `.${ANIMATION_CLASS_NAME}`,
              translateX: [-cardWidth, 0],
              direction: 'alternate',
              loop: true,
              easing: 'linear',
              duration: 3000,
            }),
          500
        )
      );
    }
  };

  componentWillUnmount = () => {
    this.timeouts.forEach(clearTimeout);
  };

  isFrontDisabled = () => {
    const { insuranceCard, skipBack } = this.props;
    if (skipBack) return false;
    return insuranceCard.front.base64 && !insuranceCard.back.base64;
  };

  isFrontScanCompleted = () => {
    const {
      skipBack,
      insuranceCard: {
        front: { scanCompleted: frontScanCompleted },
        back: { scanCompleted: backScanCompleted },
      },
    } = this.props;

    return skipBack ? frontScanCompleted : frontScanCompleted && backScanCompleted;
  };

  isScanStarted = ({
    skipBack,

    insuranceCard: {
      front: { scanStarted: frontScanStarted },
      back: { scanStarted: backScanStarted },
    },
  }: any) => (skipBack ? frontScanStarted : frontScanStarted && backScanStarted);

  render = () => {
    const {
      insuranceCard,
      onClickBack,
      onClickFront,
      handleFileChange,
      skipBack,
      handleSkipInsurance,
      handleEnterManually,
      handleSkipBack,
      location,
      origin,
      booking,
      hideCta,
      theme,
    } = this.props;
    const { t } = this.props;

    const isMobileFlow = true;
    const isInsuranceFormPage = false;
    const frontActions = getAddInsuranceActions(ImageType.HealthInsuranceFront);
    const backActions = getAddInsuranceActions(ImageType.HealthInsuranceBack);
    if (booking) {
      const { isPrimary } = validateFamilyBooking(booking);
      if (isPrimary) {
        frontActions.setImageData.push(setMetaPaperworkInsuranceIdFrontImgUrl);
        frontActions.setImageId.push(setMetaPaperworkInsuranceIdFrontId);
        backActions.setImageData.push(setMetaPaperworkInsuranceIdBackImgUrl);
        backActions.setImageId.push(setMetaPaperworkInsuranceIdBackId);
      }
    }

    return (
      <>
        <WhiteLabelWrapperWithTheme theme={theme ?? 'crunchberry'}>
          <Stack alignItems="center">
            <InsuranceCard
              animationClassName={ANIMATION_CLASS_NAME}
              cardImage={insuranceCard.front.base64}
              disabled={this.isFrontDisabled()}
              handleFileChange={handleFileChange(
                ImageType.HealthInsuranceFront,
                location,
                frontActions
              )}
              isSRPorCDP={this.props.isSRPorCDP}
              onClick={onClickFront}
              parentControlAnimation={!skipBack}
              placeholderCopy={t('addInsurance:mobileAddInsurance.placeOnFlatSurface')}
              placeholderImage="/images/insurance/front_copy.svg"
              scanCompleted={this.isFrontScanCompleted()}
              scanStarted={insuranceCard.front.scanStarted}
              showPlaceholder
            >
              {t('addInsurance:mobileAddInsurance.frontOfCard')}
            </InsuranceCard>

            {!skipBack && (
              <InsuranceCard
                animationClassName={ANIMATION_CLASS_NAME}
                buttonStyle={
                  theme === 'crunchberry'
                    ? ButtonVariations.tertiary
                    : ButtonVariations.pinkTertiary
                }
                cardImage={insuranceCard.back.base64}
                handleFileChange={handleFileChange(
                  ImageType.HealthInsuranceBack,
                  location,
                  backActions
                )}
                isSRPorCDP={this.props.isSRPorCDP}
                onClick={onClickBack}
                parentControlAnimation
                placeholderCopy={t('addInsurance:mobileAddInsurance.flipCardOverAnd')}
                placeholderImage="/images/insurance/back_copy.svg"
                scanCompleted={
                  insuranceCard.back.scanCompleted && insuranceCard.front.scanCompleted
                }
                scanStarted={insuranceCard.front.scanStarted && insuranceCard.back.scanStarted}
                showPlaceholder={Boolean(insuranceCard.front.base64)}
              >
                {t('addInsurance:mobileAddInsurance.backOfCard')}
              </InsuranceCard>
            )}
          </Stack>

          {!hideCta && (
            <CtaContainerGrid item xs={12}>
              <Grid container justify="center">
                <MainCtaButton
                  handleEnterManually={handleEnterManually}
                  handleSkipBack={handleSkipBack}
                  handleSkipInsurance={handleSkipInsurance}
                  insuranceCard={insuranceCard}
                  isInsuranceFormPage={isInsuranceFormPage}
                  isMobileFlow={isMobileFlow}
                  location={location}
                  origin={origin}
                  skipBack={skipBack}
                />
              </Grid>

              <Grid container justify="center">
                {this.state.showTakeTooLong && (
                  <StyledSkipInsuranceButton
                    onClick={() => handleEnterManually({ from: ENTER_MANUALLY_FROM_TIMEOUT })}
                    variation={ButtonVariations.link}
                  >
                    {t('addInsurance:mobileAddInsurance.takingTooLong')}
                  </StyledSkipInsuranceButton>
                )}
              </Grid>
            </CtaContainerGrid>
          )}
        </WhiteLabelWrapperWithTheme>
      </>
    );
  };
}

export default withTranslation(['common', 'addInsurance'])(MobileAddInsurance);
